// src/firebase/storage.js
import { initializeApp } from 'firebase/app';
import { 
  getStorage, 
  ref, 
  uploadBytesResumable, 
  getDownloadURL,
  deleteObject,
  listAll
} from 'firebase/storage';
import { 
  getFirestore, 
  collection, 
  addDoc, 
  deleteDoc, 
  doc, 
  query, 
  where,
  orderBy, 
  getDocs,
  serverTimestamp
} from 'firebase/firestore';
import firebaseConfig from './config';

// Initialize Firebase
const app = initializeApp(firebaseConfig);
const storage = getStorage(app);
const db = getFirestore(app);

// File storage paths
const FILES_COLLECTION = 'examContentFiles';
const STORAGE_PATH = 'exam-content-files';

// Upload a file to Firebase Storage and store metadata in Firestore
export const uploadFile = async (file, userId) => {
  try {
    // Create a storage reference
    const fileRef = ref(storage, `${STORAGE_PATH}/${userId}/${Date.now()}_${file.name}`);
    
    // Upload file
    const uploadTask = uploadBytesResumable(fileRef, file);
    
    // Return a promise that resolves when the upload completes
    return new Promise((resolve, reject) => {
      uploadTask.on(
        'state_changed',
        // Progress observer
        (snapshot) => {
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          console.log(`Upload progress: ${progress}%`);
        },
        // Error observer
        (error) => {
          console.error('Upload failed:', error);
          reject(error);
        },
        // Completion observer
        async () => {
          try {
            // Get download URL
            const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
            
            // Store file metadata in Firestore
            await addDoc(collection(db, FILES_COLLECTION), {
              name: file.name,
              contentType: file.type,
              size: file.size,
              storagePath: fileRef.fullPath,
              downloadUrl: downloadURL,
              userId: userId,
              uploadedAt: serverTimestamp(),
              course: 'exam-content',
              platform: 'cosknow'
            });
            
            resolve(downloadURL);
          } catch (error) {
            console.error('Error storing file metadata:', error);
            reject(error);
          }
        }
      );
    });
  } catch (error) {
    console.error('Error starting upload:', error);
    throw error;
  }
};

// Get all files for a user from Firestore
export const getFiles = async (userId) => {
  try {
    const q = query(
      collection(db, FILES_COLLECTION),
      where('userId', '==', userId),
      orderBy('uploadedAt', 'desc')
    );
    
    const querySnapshot = await getDocs(q);
    
    const files = [];
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      const uploadedAt = data.uploadedAt ? data.uploadedAt.toDate().toISOString() : null;
      
      files.push({
        id: doc.id,
        ...data,
        uploadedAt
      });
    });
    
    return files;
  } catch (error) {
    console.error('Error getting files:', error);
    throw error;
  }
};

// Delete a file from Storage and its metadata from Firestore
export const deleteFile = async (fileId, userId) => {
  try {
    // Get file metadata
    const fileRef = doc(db, FILES_COLLECTION, fileId);
    const fileSnapshot = await getDocs(query(collection(db, FILES_COLLECTION), where('__name__', '==', fileId)));
    
    if (fileSnapshot.empty) {
      throw new Error('File not found');
    }
    
    const fileData = fileSnapshot.docs[0].data();
    
    // Check if user has permission to delete
    if (fileData.userId !== userId) {
      throw new Error('Permission denied');
    }
    
    // Delete from Storage
    const storageRef = ref(storage, fileData.storagePath);
    await deleteObject(storageRef);
    
    // Delete from Firestore
    await deleteDoc(fileRef);
    
    return true;
  } catch (error) {
    console.error('Error deleting file:', error);
    throw error;
  }
};